/**
 * Plan symbols for plumbing trim: shower head, shower valve, tub spout and
 * floor drain — the twin of `electricalDrawingUtils`' device symbols, drawn the
 * same way: in WORLD units inside the device's own frame (translated to it,
 * rotated so local +x points out of its wall into the room), and sized in
 * PIXELS through `scale`, because a 15 cm shower head is a few pixels at plan
 * zoom. Stroke colour is left as the caller set it.
 *
 * The shower head is the floor plan's own mark — the two rings the `shower`
 * fixture draws for its head (`drawShowerHeadRings`, shared with
 * `objectDrawingUtils`) — set on an arm out of the wall, as the regular
 * shower draws its head.
 */

const SYMBOL_RADIUS_PX = 7;
const ARM_FRAC = 1.2; // the shower arm and the tub spout, out of the wall
const INNER_RING_FRAC = 0.5;

const PLUMBING_SYMBOL_TYPES = new Set([
  'shower-head',
  'shower-valve',
  'tub-spout',
  'floor-drain',
]);

/** True when `drawPlumbingSymbol` has a symbol for this device type. */
export const isPlumbingSymbolType = (type) =>
  PLUMBING_SYMBOL_TYPES.has(String(type || '').toLowerCase());

/**
 * A shower head seen from above: two rings, the inner one half the outer.
 * Drawn on the current path style; fills nothing.
 */
export const drawShowerHeadRings = (ctx, x, y, r) => {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, r * INNER_RING_FRAC, 0, Math.PI * 2);
  ctx.stroke();
};

/**
 * Draw a plumbing device's plan symbol. The context is already translated to
 * the device and rotated by `objectAngle`, local +x into the room.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} type - One of `PLUMBING_SYMBOL_TYPES`
 * @param {number} scale - Pixels per metre
 * @param {number} [objectAngle=0] - The frame's rotation, for upright symbols
 */
export const drawPlumbingSymbol = (ctx, type, scale, objectAngle = 0) => {
  const t = String(type || '').toLowerCase();
  const px = (n) => n / scale;
  const r = px(SYMBOL_RADIUS_PX);
  const arm = r * ARM_FRAC;

  const disc = (x, y, radius) => {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.stroke();
  };
  const line = (x1, y1, x2, y2) => {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.lineWidth = Math.max(ctx.lineWidth, px(1.5));

  if (t === 'shower-head') {
    // arm out of the wall, the head's two rings at its end
    line(0, 0, arm, 0);
    disc(arm + r, 0, r);
    drawShowerHeadRings(ctx, arm + r, 0, r);
  } else if (t === 'shower-valve') {
    // the plumbing valve symbol, two triangles tip to tip, on the line out of
    // the wall
    const h = r * 0.8;
    ctx.beginPath();
    ctx.moveTo(0, -h);
    ctx.lineTo(2 * r, h);
    ctx.lineTo(2 * r, -h);
    ctx.lineTo(0, h);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.stroke();
  } else if (t === 'tub-spout') {
    // a spout out of the wall, its outlet near the end
    const w = r * 0.9;
    const len = arm + r;
    ctx.beginPath();
    ctx.rect(0, -w / 2, len, w);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.stroke();
    disc(len - w / 2, 0, w * 0.22);
  } else if (t === 'floor-drain') {
    // a round grate, upright whatever the device's rotation
    ctx.rotate(-objectAngle);
    disc(0, 0, r);
    const g = r * 0.7;
    line(-g, 0, g, 0);
    line(0, -g, 0, g);
  }

  ctx.restore();
};
